import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2, RotateCcw, Users } from "lucide-react";
import { toast } from "sonner";
import api, { API, apiErr } from "@/lib/api";

const ACCEPT = ".xlsx,.xls,.csv";
const MAX_MB = 5;

export function LeadUploadPanel({ campaignId = "", onImported }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [colError, setColError] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/leads/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "PK_AI_Lead_Template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e, "Could not download template")); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    const okExt = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!okExt) { toast.error("Only .xlsx, .xls or .csv files are allowed"); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File exceeds ${MAX_MB}MB limit`); return; }
    setUploading(true); setSummary(null); setColError(null);
    const fd = new FormData();
    fd.append("file", file);
    if (campaignId) fd.append("campaign_id", campaignId);
    try {
      const { data } = await api.post("/leads/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(data);
      if (data.added > 0) { toast.success(`${data.added} leads imported`); onImported?.(); }
      else toast.info("No new leads were imported");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "missing_columns") setColError(detail.missing);
      else toast.error(apiErr(e, "Upload failed"));
    } finally { setUploading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); };

  if (summary) {
    return (
      <div className="space-y-4" data-testid="upload-summary">
        <Card className="border p-5">
          <div className="flex items-center gap-2 mb-4"><CheckCircle2 className="w-5 h-5 text-emerald-500" /><h3 className="font-semibold font-display text-lg">Upload Complete</h3></div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-emerald-600" data-testid="summary-added"><CheckCircle2 className="w-4 h-4" /><b>{summary.added}</b> leads added successfully</div>
            <div className="flex items-center gap-2 text-amber-600" data-testid="summary-duplicates"><AlertTriangle className="w-4 h-4" /><b>{summary.duplicates}</b> rows skipped (duplicates)</div>
            <div className="flex items-center gap-2 text-red-500" data-testid="summary-invalid"><XCircle className="w-4 h-4" /><b>{summary.invalid}</b> rows skipped (invalid)</div>
          </div>
          {summary.skipped?.length > 0 && (
            <div className="mt-4 border-t pt-3">
              <p className="text-xs font-medium text-slate-500 mb-2">Skipped details:</p>
              <div className="max-h-48 overflow-y-auto space-y-1 text-xs" data-testid="summary-skipped-list">
                {summary.skipped.map((s, i) => (
                  <div key={`${s.row}-${i}`} className="text-slate-600 dark:text-slate-400">Row {s.row}: {s.reason}</div>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-5">
            <Button onClick={() => onImported?.()} data-testid="view-imported-btn"><Users className="w-4 h-4 mr-1" />View Imported Leads</Button>
            <Button variant="outline" onClick={() => { setSummary(null); setColError(null); }} data-testid="upload-another-btn"><RotateCcw className="w-4 h-4 mr-1" />Upload Another</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">Import your leads from an Excel or CSV file.</p>
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="w-4 h-4 mr-1" />Download Template</Button>
      </div>

      {colError && (
        <Card className="border border-red-300 bg-red-50 dark:bg-red-950/30 p-4" data-testid="upload-column-error">
          <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
            <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Upload Failed — Missing required columns: {colError.join(", ")}</p>
              <p className="mt-1 text-red-600 dark:text-red-400">Your file must have these columns: Enquiry Date, First Name, Mobile</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={downloadTemplate}><Download className="w-3.5 h-3.5 mr-1" />Download Template</Button>
            </div>
          </div>
        </Card>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${drag ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10" : "border-slate-300 dark:border-slate-700 hover:border-indigo-400"}`}
        data-testid="upload-dropzone"
      >
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="upload-file-input" />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-slate-500"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /><span className="text-sm">Processing your file…</span></div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center"><UploadCloud className="w-6 h-6 text-indigo-600" /></div>
            <p className="text-sm font-medium">Drag & drop your file here, or <span className="text-indigo-600 underline">Browse File</span></p>
            <p className="text-xs text-slate-400">Accepted: .xlsx, .xls, .csv · Max {MAX_MB}MB</p>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500">Download the template above, fill your leads, and upload here.</p>

      <Card className="border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 p-4">
        <div className="flex items-center gap-2 mb-2 text-sm font-medium"><FileSpreadsheet className="w-4 h-4 text-indigo-500" />Template Instructions</div>
        <ul className="text-xs text-slate-500 space-y-1 list-disc pl-5">
          <li>Column headers must match exactly (download template to be safe)</li>
          <li>Enquiry Date must be in DD/MM/YYYY format (e.g. 18/08/2026)</li>
          <li>Mobile must be a 10-digit Indian number (format as Text in Excel)</li>
          <li>Email and Location are optional</li>
          <li>Duplicate mobile numbers will be automatically skipped</li>
          <li>Maximum 500 leads per upload</li>
        </ul>
      </Card>
    </div>
  );
}
